import React, { useCallback, useEffect, useRef, useState } from "react";

import type { GeneratedImage } from "@/types";

interface UseImageScrollingProps<TImage extends GeneratedImage> {
  generatedImages: TImage[];
  currentImageIndex: number;
  isAvatarGenerating: boolean;
  onImageSelect: (selectedImage: TImage, index: number) => void;
  isVisible?: boolean;
  imageWidth?: number;
  containerGap?: number;
}

export const useImageScrolling = <TImage extends GeneratedImage>({
  generatedImages,
  currentImageIndex,
  isAvatarGenerating,
  onImageSelect,
  isVisible = true,
  imageWidth = 200,
  containerGap = 10,
}: UseImageScrollingProps<TImage>) => {
  const [hasMoved, setHasMoved] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const startPositionRef = useRef(0);
  const moveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper function to clear timeout
  const clearMoveTimeout = useCallback(() => {
    if (moveTimeoutRef.current) {
      clearTimeout(moveTimeoutRef.current);
      moveTimeoutRef.current = null;
    }
  }, []);

  // Stores initial position
  const startDragScrolling = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;

      setIsDragging(true);
      setHasMoved(false);

      startPositionRef.current = e.clientX;
      clearMoveTimeout();
    },
    [clearMoveTimeout],
  );

  // Updates scroll position in real-time to follow mouse movement
  const updateScrollWhileDragging = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !scrollContainerRef.current) return;

      // Mark as moved if there's significant horizontal movement
      if (Math.abs(e.clientX - startPositionRef.current) > 2) {
        setHasMoved(true);
      }

      const container = scrollContainerRef.current;
      container.scrollLeft = container.scrollLeft - (e.clientX - startPositionRef.current);
      startPositionRef.current = e.clientX;
    },
    [isDragging],
  );

  // Finalizes drag interaction and prevents accidental image clicks
  const stopDragScrolling = useCallback(() => {
    setIsDragging(false);

    // Small delay before allowing selection after movement
    if (hasMoved) {
      moveTimeoutRef.current = setTimeout(() => {
        setHasMoved(false);
      }, 150);
    }
  }, [hasMoved]);

  const resetForTouchScrolling = useCallback(() => {
    setHasMoved(false);
    clearMoveTimeout();
  }, [clearMoveTimeout]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", updateScrollWhileDragging);
      document.addEventListener("mouseup", stopDragScrolling);
      document.addEventListener("mouseleave", stopDragScrolling);

      return () => {
        document.removeEventListener("mousemove", updateScrollWhileDragging);
        document.removeEventListener("mouseup", stopDragScrolling);
        document.removeEventListener("mouseleave", stopDragScrolling);
      };
    }
  }, [isDragging, updateScrollWhileDragging, stopDragScrolling]);

  useEffect(() => {
    return () => {
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }
    };
  }, []);

  // Provides quick navigation back to first image position
  const scrollToStart = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    container.scrollTo({ left: 0, behavior: "smooth" });
  }, []);

  // Centers target image in viewport for optimal viewing
  const scrollToImage = useCallback(
    (imageIndex: number) => {
      if (!scrollContainerRef.current) return;

      const container = scrollContainerRef.current;
      const imageWithGap = imageWidth + containerGap;

      // Calculate the position to center the image in the viewport
      const containerWidth = container.clientWidth;
      const actualImageIndex = isAvatarGenerating ? imageIndex + 1 : imageIndex;
      const imageStartPosition = actualImageIndex * imageWithGap;

      // Center the image in the viewport
      const targetScrollLeft = imageStartPosition - containerWidth / 2 + imageWidth / 2;

      // Ensure we don't scroll beyond the boundaries
      const maxScrollLeft = container.scrollWidth - containerWidth;
      const finalScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

      container.scrollTo({ left: finalScrollLeft, behavior: "smooth" });
    },
    [isAvatarGenerating, imageWidth, containerGap],
  );

  const navigateToNextImage = useCallback(() => {
    if (generatedImages.length === 0) return;

    // Don't loop - stop at the last image
    if (currentImageIndex >= generatedImages.length - 1) return;

    const nextIndex = currentImageIndex + 1;
    const nextImage = generatedImages[nextIndex];
    onImageSelect(nextImage, nextIndex);

    scrollToImage(nextIndex);
  }, [currentImageIndex, generatedImages, onImageSelect, scrollToImage]);

  const navigateToPrevImage = useCallback(() => {
    if (generatedImages.length === 0) return;

    if (currentImageIndex <= 0) return;

    const prevIndex = currentImageIndex - 1;
    const prevImage = generatedImages[prevIndex];
    onImageSelect(prevImage, prevIndex);

    scrollToImage(prevIndex);
  }, [currentImageIndex, generatedImages, onImageSelect, scrollToImage]);

  const selectImageIfNotScrolling = useCallback(
    (selectedImage: TImage, index: number) => {
      // Don't select image if user was scrolling
      if (hasMoved) return;

      onImageSelect(selectedImage, index);
    },
    [hasMoved, onImageSelect],
  );

  const containerProps = {
    ref: scrollContainerRef,
    onMouseDown: startDragScrolling,

    onTouchStart: resetForTouchScrolling,
  };

  // Provides arrow key navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isVisible || generatedImages.length === 0) return;

      // Don't interfere with input/textarea navigation
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA" || activeElement.getAttribute("contenteditable") === "true")
      ) {
        return;
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        navigateToPrevImage();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        navigateToNextImage();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isVisible, generatedImages.length, navigateToNextImage, navigateToPrevImage]);

  // scrolls to selected image when reopening modal
  useEffect(() => {
    if (isVisible && generatedImages.length > 0 && currentImageIndex >= 0) {
      scrollToImage(currentImageIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, generatedImages.length]);

  return {
    isDragging,
    hasMoved,
    scrollContainerRef,
    navigateToNextImage,
    navigateToPrevImage,
    scrollToImage,
    scrollToStart,
    handleImageSelect: selectImageIfNotScrolling,
    containerProps,
  };
};
